import log from "loglevel";
import { useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import * as constants from "Constants";
import httpService from "services/httpService";

function getExamWsUrl(labId) {
  if (!constants.base_url || !labId) return null;
  try {
    const normalizedUrl = constants.base_url.replace(/\/api\/?$/, "");
    const url = new URL(normalizedUrl);
    const protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${url.host}/ws/exam/${labId}`;
  } catch {
    return null;
  }
}

/**
 * WebSocket hook for instructor live dashboard updates.
 *
 * Connects to the same exam WS endpoint as students. The backend detects
 * the instructor role and joins them to a dedicated instructor group that
 * receives real-time dashboard_update events.
 *
 * @param {Object} options
 * @param {string|number} options.labId - The exam/lab ID
 * @param {function} options.onDashboardUpdate - Callback for dashboard_update messages
 * @returns {{ isConnected: boolean }}
 */
export default function useInstructorExamWebSocket({ labId, onDashboardUpdate }) {
  const MAX_RECONNECT_TRY = 5;
  const PING_INTERVAL_MS = 30000;

  const token = httpService.defaults.headers.common["Authorization"] || localStorage.getItem("token");
  const wsUrl = labId ? getExamWsUrl(labId) : null;
  const totalReconnectTry = useRef(0);
  const pingIntervalRef = useRef(null);
  const onDashboardUpdateRef = useRef(onDashboardUpdate);

  useEffect(() => {
    onDashboardUpdateRef.current = onDashboardUpdate;
  }, [onDashboardUpdate]);

  const { sendJsonMessage, readyState } = useWebSocket(
    wsUrl,
    {
      onOpen: () => {
        totalReconnectTry.current = 0;
        sendJsonMessage({ type: "authenticate", token });
      },
      onMessage: (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.status === "unauthorized") {
            log.error("Instructor WS authentication failed — token may be expired.");
            return;
          }

          if (data.type === "dashboard_update") {
            log.debug("WS dashboard_update received:", data.event);
            if (onDashboardUpdateRef.current) {
              onDashboardUpdateRef.current(data);
            }
          }
        } catch (e) {
          log.warn("Failed to parse WS message:", e);
        }
      },
      shouldReconnect: (closeEvent) => {
        if (closeEvent.code >= 4000) return false;
        if (totalReconnectTry.current >= MAX_RECONNECT_TRY) return false;
        totalReconnectTry.current++;
        return true;
      },
      reconnectAttempts: MAX_RECONNECT_TRY,
    }
  );

  // Ping keepalive
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      pingIntervalRef.current = setInterval(() => {
        sendJsonMessage({ type: "ping" });
      }, PING_INTERVAL_MS);
    }

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [readyState, sendJsonMessage]);

  const isConnected = readyState === ReadyState.OPEN;

  return { isConnected };
}
